import 'react-datepicker/dist/react-datepicker.css';

import { Forward5, Forward10, Forward30 } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ClearIcon from '@mui/icons-material/Clear';
import EventIcon from '@mui/icons-material/Event';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Frequency, RRule } from 'rrule';

import { useProgress } from '@/contexts/ProgressProvider';

import { REPEAT_List } from '../../constants';
import { ChannelType } from '../../enums';
import type { Category, Item, LiveProgram } from '../../models';
import type { ChannelMetaData, GraphicsOverlayConfig } from '../../models/channel';
import { CustomAttributes } from '../../models/customAttributes';
import type { ValidationError } from '../../models/ValidationError';
import { ValidationErrors } from '../../models/ValidationErrors';
import AssetDialogHeader from '../../pages/assets/asset-details-modal/AssetDialogHeader';
import { FuelEntityType, FuelPortalService } from '../../services';
import { SchemaType } from '../../store/forms/Asset-Editor/SchemaTypeOptions';
import type { ChannelForm } from '../../store/forms/Channel/ChannelForm';
import { applicationDataSelector } from '../../store/slices/application-data';
import useAppUtils from '../../utils/appUtils';
import calendarUtils from '../../utils/calendarUtils';
import clientUtils from '../../utils/clientUtils';
import { compressionUtils } from '../../utils/compressionUtils';
import { dateUtils } from '../../utils/dateUtils';
import itemUtils from '../../utils/itemUtils';
import FuelAutoComplete from '../fuel-autocomplete';
import VNAutocomplete from '../VNAutocomplete/VNAutocomplete';
import MetadataContent from '../MetadataContent';
import TimeInput from '../time-input/TimeInput';
import type { EventForm } from './EventForm';
import { RruleOptions } from './EventForm';
import FuelCalendarHelper from './FuelCalendarHelper';
import Daily from './Repeat/Daily';
import Monthly from './Repeat/Monthly';
import Weekly from './Repeat/Weekly';
import Yearly from './Repeat/Yearly';

interface IProps {
  // open: boolean;

  onClose: () => void;
  onSave: (formValues: any) => void;
  categories?: Category[];
  channelFormValues?: any;
  // hideRecurrenceEditor: boolean;
  clips?: Item[];
  // allLivePrograms?: LiveProgram[];
  editor?: string;
}

const EditIcon = ({ onChangeFieldDictionary, fieldName, disabled }) => {
  return (
    <IconButton className={disabled ? 'disabled' : ''} size="small" onClick={() => onChangeFieldDictionary(fieldName)}>
      <i className="fa fa-pencil-square-o" />
    </IconButton>
  );
};

const AddEvent = (
  {
    // open,
    onClose,
    onSave,
    categories = [],
    channelFormValues = null,
    clips = [],
    // allLivePrograms = [],
    editor = 'ChannelEditor',
  }: IProps,
  ref
) => {
  const [allowMultiCategorySelect, setAllowMultiCategorySelect] = useState(true);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [errors, setErrors] = useState([]);
  const { vnConfirm, alert } = useAppUtils();
  const [selectedRepeat, setSelectedRepeat] = useState({
    id: 'never',
    name: 'Never',
  });
  const [disableEndDate, setDisableEndDate] = useState(false);
  const [disableExtendDuration, setDisableExtendDuration] = useState(false);
  const [selectedClip, setSelectedClip] = useState<any>('');
  const [selectedLiveProgram, setSelectedLiveProgram] = useState('');
  const [showGraphicsUrl, setShowGraphicsUrl] = useState(false);
  const { ouData } = useSelector(applicationDataSelector);
  const [assets, setAssets] = useState<Item[]>(clips);
  const [thumbnailSrc, setThumbnailSrc] = useState<string>('');
  const channelType = useMemo(() => (channelFormValues ? channelFormValues.getValues('type') : 'None'), [channelFormValues]);
  const [eventForm, setEventForm] = useState();
  const [open, setOpen] = useState(false);
  const [hideRecurrenceEditor, setHideRecurrenceEditor] = useState(false);
  const [rightOpen, setRightOpen] = React.useState(false);
  const [customAttributes, setCustomAttributes] = useState(CustomAttributes.Create());
  const [allLivePrograms, setAllLivePrograms] = useState<LiveProgram[]>([]);
  const { loading, setProgress } = useProgress();

  const formValues = useForm<EventForm>({
    defaultValues: useMemo(() => {
      // console.log('formValues changed', eventForm);
      return eventForm;
    }, [eventForm]),
  });
  const [timezone, setTimeZone] = useState(eventForm && eventForm.timezone ? eventForm.timezone : null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const minDate = DateTime.now().setZone(timezone).startOf('minute');
  const flags = useFlags();
  const { watch } = formValues;
  const watchFieldDictionary = watch('fieldDictionary');
  const watchEndDate = watch('end');
  // console.log('watchFieldDictionary', watchFieldDictionary);

  useImperativeHandle(ref, () => ({
    open(eventForm, hideRecurrenceEditor) {
      // console.log('open Add Event Dialig', {eventForm, hideRecurrenceEditor});
      setEventForm(eventForm);
      formValues.reset(eventForm);
      setHideRecurrenceEditor(hideRecurrenceEditor);
      setOpen(true);
    },
    close() {
      setOpen(false);
      setEventForm(null);
      setErrors([]);
      setSelectedClip('');
      setThumbnailSrc(null);
      setSelectedRepeat({
        id: 'never',
        name: 'Never',
      });
    },
  }));

  const handleCategoriesChange = (values) => {
    const arr = itemUtils.ensureArray(values);
    const names = arr.map((v) => (typeof v === 'object' && v?.name != null ? v.name : String(v)));
    const categoryObjects = filteredCategories.filter((c) => names.includes(c.name));
    formValues.setValue('categories', names);
    setSelectedCategories(categoryObjects);
    onFormChange(formValues);
  };

  const handleClipChange = async (value) => {
    // console.log('handleClipChange', { value });
    setProgress(true);
    try {
      if (value) {
        if (
          (value.data.businessMetadata.embargoDate && new Date(value.data.businessMetadata.embargoDate) > new Date()) ||
          (value.data.businessMetadata.expireDate && new Date(value.data.businessMetadata.expireDate) <= new Date())
        ) {
          alert('Embargo or Expired assets cannot be used in a Broadcast channel.', 'warning');
          return;
        }
      }
      let event = formValues.getValues();
      event = await FuelCalendarHelper.getClipOrNestedChannelEvent(event, value, channelFormValues);

      // set thumbnail image
      const tempThumbnail = itemUtils.resolveThumbnailSrc(value, '');
      if (tempThumbnail && tempThumbnail !== '') {
        setThumbnailSrc(tempThumbnail);
      } else {
        setThumbnailSrc(null);
      }

      setSelectedClip(value);
      formValues.setValue('item', value);
      formValues.reset(event);
      onFormChange(formValues);
      setEndDate(calendarUtils.setOtherZone(formValues.getValues('end'), timezone));
      await loadForm();
      toValidate(formValues);
    } finally {
      setProgress(false);
    }
  };

  const handleLiveProgramChange = (value) => {
    // console.log('handleLiveProgramChange', { value });
    const eventForm = formValues.getValues();
    const attachedEntityType = FuelEntityType.LiveProgram;
    const attachedFuelEntityId = clientUtils.getAttachedFuelEntityId(value);
    const categories = value && value.metadata && value.metadata.categories ? value.metadata.categories.map((c) => c.name) : [];
    const eventData = {
      ...eventForm,
      title: editor === 'Input' ? value.name : eventForm.title || value.name,
      id: eventForm.id ? eventForm.id : value.id,
      categories,
      color: '#ff4147',
      attachedFuelEntityType: attachedEntityType,
      attachedFuelEntityId,
    };

    // set thumbnail image
    const tempThumbnail = value?.metadata?.thumbnail?.previewUri;
    if (tempThumbnail && tempThumbnail !== '') {
      setThumbnailSrc(tempThumbnail);
    } else {
      setThumbnailSrc(null);
    }

    setSelectedLiveProgram(value);
    formValues.reset(eventData);
  };
  const handleRepeatChange = (value) => {
    const getRule = (enumValue) => {
      const newRule = new RruleOptions();
      // #446006 EPG_Issue: Issue with creating a recurring event using weekly option.  Set all events for week to start on Sunday unless already set in ics
      newRule.wkst = RRule.SU;
      newRule.freq = enumValue;
      //  newRule.tzid = formValues.getValues('timezone');
      return newRule;
    };
    setSelectedRepeat(value);
    if (value.id === 'never') {
      formValues.setValue('rrule', null);
    } else {
      formValues.setValue('rrule', getRule(value.enumValue));
    }
    const rrule = formValues.getValues('rrule');
    if (rrule && rrule.freq === Frequency.MONTHLY && (!rrule.bymonthday || !rrule.bysetpos)) {
      rrule.bymonthday = new Date().getDate();
      formValues.setValue('rrule.bymonthday', rrule.bymonthday);
      formValues.setValue('monthMode', 'onMonthDay');
    } else if (rrule && rrule.freq === Frequency.YEARLY && (!rrule.bymonthday || !rrule.bysetpos || !rrule.bymonth)) {
      rrule.bymonthday = new Date().getDate();
      formValues.setValue('rrule.bymonthday', rrule.bymonthday);
      formValues.setValue('monthMode', 'onMonthDay');
      formValues.setValue('rrule.bymonth', new Date().getMonth() + 1);
    }
    onFormChange(formValues);
  };
  const fieldOnChange = (field, value) => {
    formValues.setValue(field, value);
    if (onFormChange) {
      onFormChange(formValues);
    }
  };

  const onStartDateChange = async (date) => {
    // console.log('date', date, timezone);
    const newDate = dateUtils.convertDateLocalToTimeZoneSpecificJSDate(date, timezone);
    if (newDate) {
      formValues.setValue('start', newDate);
      setStartDate(date);
      if (selectedClip && selectedClip.id && channelFormValues !== null) {
        setProgress(true);
        const channelValues: ChannelForm = channelFormValues.getValues();
        const data: ChannelMetaData = {};
        data.variantCodes = channelValues.defaultVariants;
        if (channelValues.showAds) {
          data.adConfiguration = channelValues.adsSponsorsForm;
        }
        const durationResponse = await FuelPortalService.getClipDuration(selectedClip, data);
        let newDuration = selectedClip.duration;
        if (durationResponse) {
          const duration = durationResponse.totalExpectedClipRuntime;
          if (duration > 0) {
            newDuration = duration;
          }
        }
        formValues.setValue('item', selectedClip);
        const newEnd = DateTime.fromJSDate(date).plus({ seconds: newDuration }).toJSDate();
        // const newEndDate = DateTime.fromJSDate(date).plus({seconds:newDuration}).toJSDate();
        // console.log('newEndDate', {newEnd, newEndDate})
        formValues.setValue('end', dateUtils.convertDateLocalToTimeZoneSpecificJSDate(newEnd, timezone));
        setEndDate(newEnd);
        formValues.setValue('duration', newDuration);
        setProgress(false);
      } else {
        formValues.setValue('item', null);
        const endDate = DateTime.fromJSDate(date).plus({ hour: 1 }).toJSDate();
        formValues.setValue('end', dateUtils.convertDateLocalToTimeZoneSpecificJSDate(endDate, timezone));
        formValues.setValue('duration', calendarUtils.getDuration(date, endDate));
        setEndDate(endDate);
      }
      onFormChange(formValues);
    }
  };

  const endDateChange = (date) => {
    if (date) {
      const newDate = dateUtils.convertDateLocalToTimeZoneSpecificJSDate(date, timezone);
      setEndDate(date);
      formValues.setValue('end', newDate);
      formValues.setValue('duration', calendarUtils.getDuration(formValues.getValues('start'), newDate));
    }
  };

  const durationChange = (value) => {
    if (value) {
      const newDate = calendarUtils.setOtherZone(formValues.getValues('start'), timezone);
      const newEnd = DateTime.fromJSDate(newDate).plus({ seconds: value }).toJSDate();
      endDateChange(newEnd);
    } else {
      formValues.setValue('duration', value);
    }
  };

  const addToDuration = (e, value: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (value) {
      const newDate = calendarUtils.setOtherZone(formValues.getValues('end'), timezone);
      const newEnd = DateTime.fromJSDate(newDate).plus({ seconds: value }).toJSDate();
      endDateChange(newEnd);
    }
  };

  const onFormChange = (form) => {
    const newEventForm: EventForm = cloneDeep(form.getValues());

    if (newEventForm && newEventForm.rrule) {
      // 1. Handle 'until' sanitization
      if (newEventForm.rrule.until) {
        // Ensure it's a native Date object.
        // If using Day.js/Moment, use .toDate()
        const untilDate = new Date(newEventForm.rrule.until);

        if (!isNaN(untilDate.getTime())) {
          newEventForm.rrule.until = untilDate;
        } else {
          delete newEventForm.rrule.until; // Remove if invalid
        }
      } else {
        // rrule library dislikes empty strings or nulls for 'until'
        // It's safer to delete the key if not in use
        delete newEventForm.rrule.until;
      }

      // 2. Clear dtstart for string generation if that's your logic
      newEventForm.rrule.dtstart = null;

      // 3. Generate the string
      try {
        const newRule = new RRule(newEventForm.rrule);
        newEventForm.recurrenceRule = newRule.toString();
      } catch (e) {
        console.error('RRule generation failed:', e);
      }

      // 4. Restore dtstart for form state
      newEventForm.rrule.dtstart = dateUtils.convertDateStrLocalToTimeZoneSpecificDate(newEventForm.start, timezone);
    }

    formValues.reset(newEventForm);
  };

  const submit: SubmitHandler<EventForm> = async (data: EventForm) => {
    const isValid = toValidate(data);
    setErrors(isValid);
    if (isValid.errors && isValid.errors.length > 0) {
      const message = `Please fix the errors before saving: ${isValid.errors[0].message}`;
      alert(message, 'warning');
      return;
    }
    onSave(data);
    setSelectedLiveProgram(null);
    // onClose();
  };

  const toValidate = (data: EventForm) => {
    // console.log('toValidate', { channelFormValues, formValues: formValues.getValues() });
    const errors: Array<ValidationError> = [];
    const dif = dateUtils.getDifferenceFromDates(data.start, data.end);

    const now = new Date().getTime();
    const startTime = new Date(data.start).getTime();
    const endTime = new Date(data.end).getTime();
    const twoMinutes = 2 * 60 * 1000;
    const isEditMode = formValues.getValues('mode') === 'existing';

    if (data.title == null || data.title.length <= 0) {
      errors.push({ fieldId: 'title', message: 'Event Name is required.' });
    }
    if (data.title && data.title.length > 67) {
      errors.push({ fieldId: 'title', message: 'Event Name should not be longer than 67 characters.' });
    }
    // if (data.subTitle && data.subTitle.length > 67) {
    //   errors.push({ fieldId: 'subTitle', message: 'Subtitle should not be longer than 67 characters.' });
    // }
    if (data.start === null) {
      errors.push({ fieldId: 'start', message: 'Start Date is required.' });
    }
    if (data.end === null) {
      errors.push({ fieldId: 'start', message: 'Start Date is required.' });
    }
    if (new Date(data.start).getTime() === new Date(data.end).getTime()) {
      errors.push({ fieldId: 'start', message: 'Start Date and End date cannot be at the same time.' });
      errors.push({ fieldId: 'end', message: 'Start Date and End date cannot be at the same time.' });
    }
    if (new Date(data.start).getTime() >= new Date(data.end).getTime()) {
      errors.push({ fieldId: 'start', message: 'Start Date should not be greater than End date.' });
    }
    //
    if (editor === 'Input') {
      // Block creating new event in the past
      if (!isEditMode && now >= startTime) {
        errors.push({
          fieldId: 'start',
          message: 'An event cannot begin at any time in the past.',
        });
      }

      if (isEditMode) {
        //  Event already ended
        if (now >= endTime) {
          errors.push({
            fieldId: 'end',
            message: 'Cannot edit an event that has already ended.',
          });
        }

        // Within last 2 minutes (but not ended yet)
        else if (now >= endTime - twoMinutes) {
          errors.push({
            fieldId: 'end',
            message: 'Editing is not allowed within 2 minutes before the event ends.',
          });
        }
      }
    }
    if (dif.days > 0) {
      errors.push({ fieldId: 'end', message: 'A single event cannot be longer than 24 hours.' });
    }
    if (data.duration === null) {
      errors.push({ fieldId: 'duration', message: 'Duration is required.' });
    }
    if (
      channelFormValues &&
      channelFormValues.getValues('isBroadcastChannel') &&
      formValues.getValues('attachedFuelEntityType') !== 'LiveProgram' &&
      formValues.getValues('attachedFuelEntityType') !== 'AdBreak' &&
      !selectedClip
    ) {
      errors.push({ fieldId: 'selectedClip', message: 'Clip is required' });
    }

    if (editor === 'Input' && !selectedLiveProgram) {
      errors.push({ fieldId: 'selectedClip', message: 'Live Program is required' });
    }

    if (data.rrule && data.rrule.count && data.rrule.count < 1) {
      errors.push({ fieldId: 'rrule.count', message: 'Count should be greater than 0' });
    }
    if (selectedRepeat.name !== 'Never' && data.rrule && !data.rrule.interval) {
      errors.push({ fieldId: 'rrule.interval', message: 'Repeat Every Day(s) should be greater than 0' });
    }

    // Todo date validation -

    return new ValidationErrors(errors);
  };
  const onChange = (fieldName, event, fieldType) => {
    let value = null;
    if (fieldType === 'toggle') {
      value = event && event.target ? event.target.checked : false;
    } else {
      value = event.target.value;
    }
    // console.log('onChange', fieldName, fieldType, value);
    // formValues[fieldName] = value;

    if (fieldName === 'attachedFuelGraphicsConfig') {
      let attachedFuelGraphicsConfigObj: GraphicsOverlayConfig = {};
      const attachedFuelGraphicsConfig = formValues.getValues('attachedFuelGraphicsConfig');
      if (attachedFuelGraphicsConfig != null) {
        try {
          const decodedGraphicsConfig = compressionUtils.base64Decode(attachedFuelGraphicsConfig);
          attachedFuelGraphicsConfigObj = JSON.parse(decodedGraphicsConfig);
        } catch (error) {
          attachedFuelGraphicsConfigObj = {};
        }
        if (attachedFuelGraphicsConfigObj == null) {
          attachedFuelGraphicsConfigObj = {};
        }
      }
      attachedFuelGraphicsConfigObj.url = value;
      const json = JSON.stringify(attachedFuelGraphicsConfigObj);
      const encodedJSON = compressionUtils.base64Encode(json);
      formValues.setValue('attachedFuelGraphicsConfig', encodedJSON, { shouldValidate: true });
      formValues.setValue('graphicsUrl', value);
    } else if (fieldName === 'enableNestedVideoMetaData') {
      const fuelEventFlags = `enableNestedVideoMetaData=${value}`;
      formValues.setValue('attchedFuelEventFlags', fuelEventFlags, { shouldValidate: true });
      if (value) {
        setRightOpen(false);
      }
      formValues.setValue(fieldName, value, { shouldValidate: true });
    } else {
      formValues.setValue(fieldName, value, { shouldValidate: true });
    }
    onFormChange(formValues);
  };

  const loadForm = async () => {
    // console.log('loadForm', { channelFormValues, formValues: formValues.getValues(), clips });
    setProgress(true);
    try {
      const rrule = formValues.getValues('rrule');
      const myItems = cloneDeep(assets);
      setDisableEndDate(false);

      if (
        formValues.getValues('attachedFuelEntityType') === 'Clip' ||
        formValues.getValues('attachedFuelEntityType') === 'ClipGroup' ||
        formValues.getValues('attachedFuelEntityType') === 'NestedChannel'
      ) {
        const clipId = clientUtils.parseFuelEntityId(formValues.getValues('attachedFuelEntityId'));
        let clip = myItems.find((c) => c.id === clipId);
        if (!clip && clipId) {
          try {
            clip = await FuelPortalService.getEntityById(FuelEntityType.Clip, clipId);
            if (clip) {
              myItems.push(clip);
            }
            setAssets(myItems);
          } catch (e) {
            alert('Unable to get clip', 'warning');
          }
        }
        if (!clip) {
          alert('Clip does not exist', 'warning');
        } else {
          const tempThumbnail = itemUtils.resolveThumbnailSrc(clip, '');
          if (tempThumbnail && tempThumbnail !== '') {
            // console.log('setting thumbnail', { tempThumbnail });
            setThumbnailSrc(tempThumbnail);
          } else {
            setThumbnailSrc(null);
          }
          if (clip && clip.schemaType === SchemaType.Video && formValues.getValues('attachedFuelEntityType') === 'Clip') {
            setDisableEndDate(true);
          }
          if (clip.data && clip.data.timeStampMarkers && clip.data.timeStampMarkers.length > 0) {
            const timeStamps = clip.data.timeStampMarkers[0].timeStamps?.map((t) => clientUtils.formatVideoTime(t));
            formValues.setValue('timeStamps', timeStamps?.join('\n'));
          } else {
            formValues.setValue('timeStamps', '');
          }
          setSelectedClip(clip);
          formValues.setValue('item', clip);
          if (!formValues.getValues('attachedFuelDisplayClassName')) {
            formValues.setValue('attachedFuelDisplayClassName', clip.source);
          }
        }
      }
      if (editor === 'Input' || editor === 'ChannelEditor') {
        const id = clientUtils.parseFuelEntityId(formValues.getValues('attachedFuelEntityId'));
        const lp = allLivePrograms.find((lp) => lp.id === id);
        setSelectedLiveProgram(lp);
        // show thumbnails for live programs
        if (lp) {
          const tempThumbnail = lp.metadata?.thumbnail?.previewUri;
          if (tempThumbnail && tempThumbnail !== '') {
            // console.log('setting thumbnail', { tempThumbnail });
            setThumbnailSrc(tempThumbnail);
          } else {
            setThumbnailSrc(null);
          }
          if (!formValues.getValues('attachedFuelDisplayClassName')) {
            formValues.setValue('attachedFuelDisplayClassName', FuelEntityType.LiveProgram);
          }
        }
      }

      if (rrule) {
        const repeat = REPEAT_List.find((item) => item.enumValue === rrule.freq);
        setSelectedRepeat(repeat);
        formValues.setValue('rrule.interval', rrule.interval);
      }
      if (formValues.getValues('categories')) {
        const categoryNames = formValues.getValues('categories');
        const categoriesNew = categories.filter(function (item) {
          return categoryNames.indexOf(item.name) >= 0;
        });
        setSelectedCategories(categoriesNew);

        // Set allowMultipleCategories for events we always allow multiple categoires
        // const allowMultipleCategories = ouData?.metaData?.allowMultipleCategories !== false;
        // if (allowMultipleCategories === true || categoriesNew?.length > 1) {
        //   setAllowMultiCategorySelect(true);
        // } else {
        //   setAllowMultiCategorySelect(false);
        // }

        // Set categories
        const myFilteredCategories = itemUtils.getFilteredCategories(categories, categoriesNew);
        setFilteredCategories(myFilteredCategories);
      } else {
        setFilteredCategories(categories);
        setSelectedCategories([]);
      }
      setCustomAttributes(formValues.getValues('customAttributes'));
    } finally {
      setProgress(false);
    }
  };

  const onInputChange = (event) => {
    setCustomAttributesData(event.target.name, event.target.value);
  };
  const onCustomAttributeFieldBlur = (event) => {
    const customFieldName = event && event.target && event.target.hasAttribute('name') ? event.target.getAttribute('name') : null;
    // console.log('customFieldName', customFieldName);
    setCustomAttributesData(customFieldName, event.target.value);
  };
  const setCustomAttributesData = (fieldName, value) => {
    const myState = cloneDeep(formValues.getValues('customAttributes'));
    myState[fieldName] = value;
    setCustomAttributes(myState);
    formValues.setValue('customAttributes', myState);
    const json = JSON.stringify(FuelCalendarHelper.removeEmptyValues(myState));
    const encodedJSON = compressionUtils.base64Encode(json);
    formValues.setValue('attachedFuelCustomAttributes', encodedJSON);
    onFormChange(formValues);
  };

  const onEventClose = () => {
    setRightOpen(false);
    onClose();
  };

  const openCreateLiveProgram = async () => {
    const isOk = await vnConfirm('Do you want to create a new live program?');
    if (isOk) {
      const win = window.open('/live/programs/editor/new', '_blank');
      if (win != null) {
        win.focus();
      }
    }
  };

  useEffect(() => {
    // console.log('AddEvent.tsx formValues changed', eventForm);
    if (eventForm) {
      const enableGraphicsOverlayFeature = flags['enable-graphics-overlay-feature'];
      setShowGraphicsUrl(enableGraphicsOverlayFeature);

      setTimeZone(eventForm.timezone);
      setStartDate(calendarUtils.setOtherZone(eventForm.start, eventForm.timezone));
      setEndDate(calendarUtils.setOtherZone(eventForm.end, eventForm.timezone));
      loadForm();
    }
  }, [eventForm]);

  useEffect(() => {
    // console.log('clips updated', clips);
    if (editor === 'ChannelEditor') {
      setAssets(clips);
    }
  }, [clips]);
  const expandRightSide = () => {
    setRightOpen(!rightOpen);
  };

  const onTitleClick = () => {};

  useEffect(() => {
    doLiveProgramsSearch();
  }, []);

  useEffect(() => {
    // disable the buttons if less than 2 minutes from end time
    if (watchEndDate) {
      const twoMin = 2 * 60 * 1000;
      const now = new Date();
      const endDateDate = new Date(watchEndDate);
      const diff = endDateDate.getTime() - now.getTime() - twoMin;
      setDisableExtendDuration(diff <= 0);
      setTimeout(() => {
        setDisableExtendDuration(true);
      }, diff);
    }
  }, [watchEndDate]);

  // -----------------------------------
  //
  // Live Programs
  //
  // ===================================
  const doLiveProgramsSearch = async () => {
    // ouId=c8801903-18a3-4564-afdc-b8ce901b1038&page=0&size=1000&keyword=&sortBy=name&orderBy=asc&enabledOnly=true&showDeleted=undefined
    setProgress(true);
    const queryParams = {
      ouId: ouData.ouId,
      page: 0,
      size: 1000,
      keyword: '',
      sortBy: 'name',
      orderBy: 'asc',
      enabledOnly: true,
      showDeleted: null,
    };
    const getListResults: any[] = await FuelPortalService.getList(FuelEntityType.LiveProgram, queryParams);
    const myLivePrograms = getListResults.data;
    // console.log('doLiveProgramsSearch', getListResults);
    setAllLivePrograms(myLivePrograms);
    setProgress(false);
  };

  const onChangeFieldDictionary = (fieldName, clearData = false, isMetaData = false) => {
    const fieldDictionary = formValues.getValues('fieldDictionary');
    if (fieldDictionary) {
      fieldDictionary[fieldName] = !clearData;
      formValues.setValue('fieldDictionary', fieldDictionary);
      if (clearData) {
        if (selectedClip && !isMetaData) {
          if (fieldName === 'description') {
            const description =
              selectedClip &&
              selectedClip.data &&
              selectedClip.data.businessMetadata &&
              selectedClip.data.businessMetadata.synopsis
                ? selectedClip.data.businessMetadata.synopsis
                : '';
            formValues.setValue('description', description);
          }
          if (fieldName === 'subTitle') {
            const subTitle =
              selectedClip &&
              selectedClip.data &&
              selectedClip.data.businessMetadata &&
              selectedClip.data.businessMetadata.subTitle
                ? selectedClip.data.businessMetadata.subTitle
                : '';
            formValues.setValue('subTitle', subTitle);
          }
          if (fieldName === 'categories') {
            const categories = selectedClip && selectedClip.categories ? selectedClip.categories.map((c) => c.name) : [];

            // Set allowMultipleCategories for events we always allow multiple categoires
            // const allowMultipleCategories = ouData?.metaData?.allowMultipleCategories !== false;
            // if (allowMultipleCategories === true || categories?.length > 1) {
            //   setAllowMultiCategorySelect(true);
            // } else {
            //   setAllowMultiCategorySelect(false);
            // }

            // Set categories
            const myFilteredCategories = itemUtils.getFilteredCategories(categories, selectedClip?.categories);
            setFilteredCategories(myFilteredCategories);

            formValues.setValue('categories', categories);
            setSelectedCategories(selectedClip.categories);
          }
          onFormChange(formValues);
        } else if (selectedClip && isMetaData) {
          const customAttributesObj = FuelCalendarHelper.extractCustomAttributesFromTags(
            selectedClip && selectedClip.tags ? selectedClip.tags : []
          );
          if (customAttributesObj && customAttributesObj.hasOwnProperty(fieldName)) {
            setCustomAttributesData(fieldName, customAttributesObj[fieldName]);
          }
        } else if (selectedLiveProgram && !isMetaData) {
          if (fieldName === 'description') {
            const { description } = selectedLiveProgram;
            formValues.setValue('description', description);
          }
          if (fieldName === 'categories') {
            const lpCategories =
              selectedLiveProgram && selectedLiveProgram.metadata && selectedLiveProgram.metadata.categories
                ? selectedLiveProgram.metadata.categories.map((c) => c.name)
                : [];

            // Set allowMultipleCategories for events we always allow multiple categoires
            // const allowMultipleCategories = ouData?.metaData?.allowMultipleCategories !== false;
            // if (allowMultipleCategories === true || lpCategories?.length > 1) {
            //   setAllowMultiCategorySelect(true);
            // } else {
            //   setAllowMultiCategorySelect(false);
            // }

            // Set categories
            const myFilteredCategories = itemUtils.getFilteredCategories(categories, selectedLiveProgram?.metadata?.categories);
            setFilteredCategories(myFilteredCategories);

            formValues.setValue('categories', lpCategories);
            setSelectedCategories(
              selectedLiveProgram.metadata && selectedLiveProgram.metadata.categories
                ? selectedLiveProgram.metadata.categories
                : []
            );
          }
          onFormChange(formValues);
        } else if (selectedLiveProgram && isMetaData) {
          const myState = cloneDeep(formValues.getValues('customAttributes'));
          if (myState && myState[fieldName]) {
            setCustomAttributesData(fieldName, '');
          }
          onFormChange(formValues);
        }
      }
    }
  };

  return (
    open && (
      <Dialog
        className="add-event-dialog"
        open={open}
        fullWidth
        maxWidth={rightOpen ? 'lg' : 'md'}
        // sx={{ height: '80vh' }}
      >
        <FormProvider {...formValues}>
          <form className="fullHeight" onSubmit={formValues.handleSubmit(submit)}>
            <Grid container>
              <Grid item xs={rightOpen ? 8 : 12}>
                <AssetDialogHeader
                  title={formValues.getValues('mode') === 'new' ? 'New Event' : 'Edit Event'}
                  onClose={onClose}
                  onTitleClick={onTitleClick}
                  rightOpen={rightOpen}
                  expandRightSide={expandRightSide}
                  showRightExpandIcon={!!(editor === 'ChannelEditor' && !formValues.getValues('enableNestedVideoMetaData'))}
                />

                <DialogContent dividers sx={{ height: '60vh' }}>
                  <Container className="no-padding" section="component" maxWidth={rightOpen ? 'lg' : 'md'}>
                    <Grid container className="section-box" spacing={2}>
                      {thumbnailSrc && (
                        <Grid item xs={12}>
                          <div className="image-thumbnail-container">
                            <img className="image-thumbnail" src={thumbnailSrc} />
                          </div>
                        </Grid>
                      )}
                      <Grid item xs={6}>
                        <TextField
                          variant="outlined"
                          required={errors && errors.has && errors.has('start')}
                          label="Event Name* (67-character limit)"
                          placeholder="Event Name"
                          fullWidth
                          size="small"
                          error={errors && errors.has && errors.has('title')}
                          value={formValues.getValues('title')}
                          onChange={(e) => onChange('title', e, 'input')}
                          inputProps={{
                            maxLength: 67,
                          }}
                        />
                        {errors && errors.has && errors.has('title') && (
                          <Typography variant="inherit" className="text-danger" test-id="ingest-title">
                            <span>{errors.message('title')}</span>
                          </Typography>
                        )}
                      </Grid>

                      <>
                        {editor !== 'Input' && (
                          <Grid
                            item
                            xs={6}
                            sx={{ display: channelType && channelType.id === ChannelType.Scheduled ? '' : 'none' }}
                          >
                            <TextField
                              variant="outlined"
                              label="Sub-title"
                              fullWidth
                              size="small"
                              placeholder="Enter Subtitle"
                              {...formValues.register('subTitle')}
                              InputProps={{
                                endAdornment:
                                  editor === 'ChannelEditor' && channelType && channelType.id === ChannelType.Scheduled ? (
                                    watchFieldDictionary.subTitle ? (
                                      <IconButton
                                        // sx={{ position: 'absolute', top: '0px', right: '0px' }}
                                        size="small"
                                        onClick={(e) => onChangeFieldDictionary('subTitle', true)}
                                      >
                                        <ClearIcon />
                                      </IconButton>
                                    ) : (
                                      <IconButton
                                        size="small"
                                        // sx={{ position: 'absolute', top: '0px', right: '0px' }}
                                        onClick={() => onChangeFieldDictionary('subTitle')}
                                      >
                                        <i className="fa fa-pencil-square-o" />
                                      </IconButton>
                                    )
                                  ) : null,
                              }}
                              disabled={!watchFieldDictionary.subTitle}
                            />
                            {errors && errors.has && errors.has('subTitle') && (
                              <Typography variant="inherit" className="text-danger" test-id="ingest-title">
                                <span>{errors.message('subTitle')}</span>
                              </Typography>
                            )}
                          </Grid>
                        )}
                        {editor === 'Input' && eventForm && eventForm.attachedFuelEntityType === 'LiveProgram' && (
                          <>
                            <Grid item xs={6} sx={{ display: 'flex' }}>
                              <div style={{ width: '100%' }}>
                                <FuelAutoComplete
                                  id="live-program-select"
                                  items={allLivePrograms}
                                  itemText="name"
                                  itemKey="id"
                                  labelText="LiveProgram"
                                  placeHolderText="Select LiveProgram"
                                  // controlWidth={250}
                                  multiSelect={false}
                                  labelPaddingTop="-5px"
                                  defaultValue={selectedLiveProgram}
                                  onChange={handleLiveProgramChange}
                                  isRequired
                                  hasError={errors && errors.has && errors.has('selectedLiveProgram')}
                                />
                              </div>
                              <IconButton onClick={doLiveProgramsSearch}>
                                <RefreshIcon titleAccess="Refresh Live programs" />
                              </IconButton>
                              <IconButton aria-label="add-live-program" onClick={(e) => openCreateLiveProgram()}>
                                <AddIcon titleAccess="Add Live Program" />
                              </IconButton>
                            </Grid>
                            {/* <Grid item xs={2} sx={{ alignContent: 'center', display: 'flex' }}>
                                 
                               
                                </Grid> */}
                            {errors && errors.has && errors.has('selectedLiveProgram') && (
                              <Typography variant="inherit" className="text-danger" test-id="ingest-title">
                                <span>{errors.message('selectedLiveProgram')}</span>
                              </Typography>
                            )}
                          </>
                        )}
                      </>
                      <Grid item xs={4}>
                        <Controller
                          control={formValues.control}
                          name="start"
                          render={({
                            field: { onChange, name, value },
                            fieldState: { invalid, isDirty }, // optional
                            formState: { errors }, // optional, but necessary if you want to show an error message
                          }) => (
                            <DatePicker
                              selected={startDate}
                              onChange={onStartDateChange}
                              showTimeSelect
                              dateFormat="MM/dd/yyyy h:mm:ss aa"
                              shouldCloseOnSelect
                              minDate={editor === 'Input' ? minDate.toJSDate() : null}
                              // popperPlacement="top-end"
                              // popperModifiers={datePickerPosition}
                              customInput={
                                <TextField
                                  size="small"
                                  key="start-date"
                                  variant="outlined"
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton size="small">
                                          <EventIcon className="date-icon" />
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                    title: 'Choose Start Datetime',
                                    className: 'embargo-expire-date-input',
                                  }}
                                  error={errors && errors.has && errors.has('start')}
                                  label="Start"
                                />
                              }
                            />
                          )}
                        />
                        {errors && errors.has && errors.has('start') && (
                          <Typography variant="inherit" className="text-danger" test-id="ingest-title">
                            <span>{errors.message('start')}</span>
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={disableEndDate ? 4 : 5}>
                        <Controller
                          control={formValues.control}
                          name="end"
                          render={({
                            field: { onChange, name, value },
                            fieldState: { invalid, isDirty }, // optional
                            formState: { errors }, // optional, but necessary if you want to show an error message
                          }) => (
                            <DatePicker
                              selected={endDate}
                              onChange={endDateChange}
                              showTimeSelect
                              dateFormat="MM/dd/yyyy h:mm:ss aa"
                              shouldCloseOnSelect
                              filterDate={(date) => date > new Date()}
                              // popperPlacement="top-end"
                              // popperModifiers={datePickerPosition}
                              customInput={
                                <TextField
                                  size="small"
                                  key="end-date"
                                  variant="outlined"
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
                                          {/* +5 */}
                                          {editor === 'Input' && (
                                            <>
                                              <IconButton
                                                size="small"
                                                onClick={(e) => addToDuration(e, 300)}
                                                disabled={disableExtendDuration}
                                                title="Extended 5 minutes"
                                              >
                                                <Forward5 />
                                              </IconButton>

                                              {/* +10 */}
                                              <IconButton
                                                size="small"
                                                title="Extended 10 minutes"
                                                onClick={(e) => addToDuration(e, 600)}
                                                disabled={disableExtendDuration}
                                              >
                                                <Forward10 />
                                              </IconButton>

                                              {/* +30 */}
                                              <IconButton
                                                size="small"
                                                title="Extended 30 minutes"
                                                onClick={(e) => addToDuration(e, 1800)}
                                                disabled={disableExtendDuration}
                                              >
                                                <Forward30 />
                                              </IconButton>
                                            </>
                                          )}

                                          {/* Calendar Icon */}
                                          <IconButton size="small">
                                            <EventIcon className="date-icon" />
                                          </IconButton>
                                        </Box>
                                      </InputAdornment>
                                    ),
                                    title: disableEndDate ? '' : 'Choose Start Datetime',
                                    className: 'embargo-expire-date-input',
                                  }}
                                  label="End"
                                />
                              }
                              disabled={disableEndDate}
                            />
                          )}
                        />
                        {errors && errors.has && errors.has('end') && (
                          <Typography variant="inherit" className="text-danger" test-id="ingest-title">
                            <span>{errors.message('end')}</span>
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={disableEndDate ? 4 : 3}>
                        <TimeInput
                          name="Duration"
                          defaultValue={formValues.getValues('duration')}
                          onTimeInputChange={(obj) => durationChange(obj.seconds)}
                          labelText="Duration"
                          isSimple={false}
                          disabled={disableEndDate}
                        />
                        {errors && errors.has && errors.has('duration') && (
                          <Typography variant="inherit" className="text-danger" test-id="ingest-title">
                            <span>{errors.message('duration')}</span>
                          </Typography>
                        )}
                      </Grid>

                      {editor === 'ChannelEditor' && (
                        <>
                          <Grid item container xs={channelType && channelType.id === ChannelType.Scheduled ? 9 : 12} spacing={2}>
                            <Grid item xs={channelType && channelType.id === ChannelType.Scheduled ? 11 : 12}>
                              <VNAutocomplete
                                label="Categories"
                                name="categories"
                                options={filteredCategories}
                                valueProp="name"
                                labelProp="name"
                                multiple={allowMultiCategorySelect}
                                allowClear
                                chipClassName="tag-chip cat"
                                limitTags={allowMultiCategorySelect ? 1 : undefined}
                                searchable="inset"
                                inputProps={{ placeholder: 'Select Categories' }}
                                disabled={!watchFieldDictionary.categories}
                                onChange={handleCategoriesChange}
                              />
                            </Grid>
                            {channelType && channelType.id === ChannelType.Scheduled && !watchFieldDictionary.categories && (
                              <Grid item xs={1} alignContent="center">
                                <EditIcon
                                  disabled={!watchFieldDictionary.categories}
                                  fieldName="categories"
                                  onChangeFieldDictionary={onChangeFieldDictionary}
                                />
                              </Grid>
                            )}
                            {channelType && channelType.id === ChannelType.Scheduled && watchFieldDictionary.categories && (
                              <Grid item xs={1} alignContent="center">
                                <IconButton size="small" onClick={(e) => onChangeFieldDictionary('categories', true)}>
                                  <ClearIcon />
                                </IconButton>
                              </Grid>
                            )}
                            <Grid
                              item
                              xs={
                                eventForm &&
                                (eventForm.attachedFuelEntityType === 'NestedChannel' ||
                                  eventForm.attachedFuelEntityType === 'ClipGroup')
                                  ? 9
                                  : 12
                              }
                            >
                              {editor === 'ChannelEditor' &&
                                eventForm &&
                                eventForm.attachedFuelEntityType !== 'LiveProgram' &&
                                eventForm.attachedFuelEntityType !== 'AdBreak' && (
                                  <FuelAutoComplete
                                    id="clip-select"
                                    items={assets}
                                    itemText="title"
                                    itemKey="id"
                                    labelText="Clip"
                                    placeHolderText="Select Clip"
                                    controlWidth={250}
                                    multiSelect={false}
                                    labelPaddingTop="-5px"
                                    defaultValue={selectedClip}
                                    onChange={handleClipChange}
                                    isRequired={channelFormValues.getValues('isBroadcastChannel')}
                                    hasError={errors && errors.has && errors.has('selectedClip')}
                                    allowEmptyValue
                                  />
                                )}
                              {errors && errors.has && errors.has('selectedClip') && (
                                <Typography variant="inherit" className="text-danger" test-id="ingest-title">
                                  <span>{errors.message('selectedClip')}</span>
                                </Typography>
                              )}
                              {editor === 'ChannelEditor' && eventForm && eventForm.attachedFuelEntityType === 'LiveProgram' && (
                                <FuelAutoComplete
                                  id="live-program-select"
                                  items={allLivePrograms}
                                  itemText="name"
                                  itemKey="id"
                                  labelText="LiveProgram"
                                  placeHolderText="Select LiveProgram"
                                  controlWidth={250}
                                  multiSelect={false}
                                  labelPaddingTop="-5px"
                                  defaultValue={selectedLiveProgram}
                                  onChange={handleLiveProgramChange}
                                />
                              )}
                            </Grid>
                            <>
                              {eventForm &&
                                (eventForm.attachedFuelEntityType === 'NestedChannel' ||
                                  eventForm.attachedFuelEntityType === 'ClipGroup') && (
                                  <Grid item xs={3}>
                                    <FormControlLabel
                                      sx={{ marginTop: '7px', marginLeft: 0 }}
                                      control={
                                        <Switch
                                          className="fuel-toggle"
                                          sx={{ marginTop: `-1px`, marginLeft: `5px` }}
                                          inputProps={{ 'aria-label': '' }}
                                          checked={formValues.getValues('enableNestedVideoMetaData')}
                                          onChange={(e) => onChange('enableNestedVideoMetaData', e, 'toggle')}
                                          size="small"
                                        />
                                      }
                                      label="Expand in EPG"
                                      labelPlacement="start"
                                    />
                                  </Grid>
                                )}
                            </>
                            <Grid item xs={12}>
                              <TextField
                                className="event-description"
                                variant="outlined"
                                label="Description"
                                fullWidth
                                placeholder="Describe your event..."
                                multiline
                                {...formValues.register('description')}
                                maxRows={8}
                                minRows={2}
                                InputProps={{
                                  endAdornment:
                                    editor === 'ChannelEditor' && channelType && channelType.id === ChannelType.Scheduled ? (
                                      watchFieldDictionary.description ? (
                                        <IconButton
                                          sx={{ position: 'absolute', top: '0px', right: '0px' }}
                                          size="small"
                                          onClick={(e) => onChangeFieldDictionary('description', true)}
                                        >
                                          <ClearIcon />
                                        </IconButton>
                                      ) : (
                                        <IconButton
                                          size="small"
                                          sx={{ position: 'absolute', top: '0px', right: '0px' }}
                                          onClick={() => onChangeFieldDictionary('description')}
                                        >
                                          <i className="fa fa-pencil-square-o" />
                                        </IconButton>
                                      )
                                    ) : null,
                                }}
                                InputLabelProps={{ shrink: true }}
                                disabled={!watchFieldDictionary.description}
                                inputProps={{ maxLength: 1000 }}
                              />
                            </Grid>
                          </Grid>
                          <Grid
                            item
                            container
                            xs={3}
                            sx={{ display: channelType && channelType.id === ChannelType.Scheduled ? '' : 'none' }}
                            spacing={2}
                          >
                            {channelType && channelType.id === ChannelType.Scheduled && (
                              <Grid item xs={12}>
                                <TextField
                                  className="midroll-ad-cue-points"
                                  variant="outlined"
                                  label="Midroll Ad Cue Points"
                                  fullWidth
                                  placeholder="Midroll Ad Cue Points..."
                                  multiline
                                  value={formValues.getValues('timeStamps')}
                                  disabled
                                  // maxRows={8}
                                  // minRows={8}
                                  InputProps={{ className: 'midroll-ad-cue-points' }}
                                />
                              </Grid>
                            )}
                          </Grid>

                          {channelType && channelType.id === ChannelType.Scheduled && showGraphicsUrl && (
                            <Grid item xs={12}>
                              <TextField
                                variant="outlined"
                                label="Graphics URL"
                                fullWidth
                                size="small"
                                placeholder="Enter Graphics Url"
                                value={formValues.getValues('graphicsUrl')}
                                onChange={(e) => onChange('attachedFuelGraphicsConfig', e, 'input')}
                                onBlur={(e) => onChange('attachedFuelGraphicsConfig', e, 'input')}
                              />
                            </Grid>
                          )}
                        </>
                      )}

                      {!hideRecurrenceEditor && (
                        <Grid item xs={6}>
                          <FuelAutoComplete
                            id="repeat-select"
                            items={REPEAT_List}
                            itemText="name"
                            itemKey="id"
                            labelText="Repeat"
                            placeHolderText=""
                            controlWidth={250}
                            multiSelect={false}
                            labelPaddingTop="-5px"
                            onChange={handleRepeatChange}
                            defaultValue={selectedRepeat}
                          />
                        </Grid>
                      )}
                      <Grid item xs={6}>
                        {selectedRepeat.name !== 'Never' && (
                          <TextField
                            variant="outlined"
                            label="Repeat Every Day(s)"
                            fullWidth
                            size="small"
                            value={formValues.getValues('rrule.interval')}
                            onKeyPress={(event) => clientUtils.keypressAndZeroValueWillNotAllow(event)}
                            onPaste={clientUtils.onPaste}
                            onChange={(e) => fieldOnChange('rrule.interval', +e.target.value)}
                            required={errors && errors.has && errors.has('rrule.interval')}
                            error={errors && errors.has && errors.has('rrule.interval')}
                          />
                        )}
                        {selectedRepeat.name !== 'Never' && errors && errors.has && errors.has('rrule.interval') && (
                          <Typography variant="inherit" className="text-danger" test-id="ingest-title">
                            <span>Repeat Every Day(s) is required</span>
                          </Typography>
                        )}
                      </Grid>
                      {selectedRepeat.name === 'Daily' && <Daily form={formValues} onFormChange={onFormChange} />}
                      {selectedRepeat.name === 'Weekly' && <Weekly form={formValues} onFormChange={onFormChange} />}
                      {selectedRepeat.name === 'Monthly' && <Monthly form={formValues} onFormChange={onFormChange} />}
                      {selectedRepeat.name === 'Yearly' && <Yearly form={formValues} onFormChange={onFormChange} />}
                      {/* <Grid item xs={12}>
              {formValues.getValues('recurrenceRule')}
            </Grid> */}
                    </Grid>
                  </Container>
                </DialogContent>
                {!rightOpen && (
                  <DialogActions>
                    <Button autoFocus variant="contained" className="cancel-button" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      variant="contained"
                      disabled={loading}
                      autoFocus
                      onClick={formValues.handleSubmit(submit)}
                    >
                      Save
                    </Button>
                  </DialogActions>
                )}
              </Grid>
              <>
                {rightOpen && (
                  <Grid item xs={4} sx={{ borderLeft: '1px solid #ddd' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                      <Box>
                        {/* <IconButton aria-label="close" onClick={onClose}>
                      <CloseIcon />
                    </IconButton> */}
                      </Box>
                      <IconButton onClick={expandRightSide}>
                        <ChevronLeftIcon />
                      </IconButton>
                    </Box>
                    <DialogContent dividers sx={{ height: '60vh' }}>
                      <MetadataContent
                        tagState={customAttributes}
                        isEditingDisabled={false}
                        onInputChange={onInputChange}
                        onSingleTagFieldBlur={onCustomAttributeFieldBlur}
                        onMultipleTagFieldBlur={onCustomAttributeFieldBlur}
                        usedFor={channelType ? channelType.id : ''}
                        fieldDictionary={watchFieldDictionary}
                        updateFiledDictionary={onChangeFieldDictionary}
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button autoFocus variant="contained" className="cancel-button" onClick={() => onEventClose()}>
                        Cancel
                      </Button>
                      <Button
                        color="primary"
                        variant="contained"
                        disabled={loading}
                        autoFocus
                        onClick={formValues.handleSubmit(submit)}
                      >
                        Save
                      </Button>
                    </DialogActions>
                  </Grid>
                )}
              </>
            </Grid>
          </form>
        </FormProvider>
      </Dialog>
    )
  );
};

export default forwardRef(AddEvent);
